/**
 * DepositNewPage - Página para crear un nuevo depósito
 * Wizard de 3 pasos: Seleccionar Cheques -> Información del Depósito -> Confirmación
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Alert,
  Chip,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from '../config/dayjs';
import { useDeposito, useCheque, useNotification } from '../context';
import { catalogoService } from '../services';
import PageHeader from '../components/common/PageHeader';
import LoadingSpinner from '../components/common/LoadingSpinner';

const steps = ['Seleccionar Cheques', 'Información del Depósito', 'Confirmación'];

const DepositNewPage = () => {
  const navigate = useNavigate();
  const { createDeposito, loading: depositLoading } = useDeposito();
  const { cheques, fetchCheques, loading: chequesLoading } = useCheque();
  const { showSuccess, showError, showWarning } = useNotification();

  // Estado del wizard
  const [activeStep, setActiveStep] = useState(0);

  // Estado de cheques seleccionados
  const [selectedCheques, setSelectedCheques] = useState([]);

  // Filtros para cheques
  const [filters, setFilters] = useState({
    banco: '',
    cliente: '',
    fechaPago: null,
  });

  // Información del depósito
  const [depositData, setDepositData] = useState({
    cuentaBancariaId: '',
    fechaDeposito: dayjs(),
    numeroBoleta: '',
    observaciones: '',
  });

  // Catálogos
  const [bancos, setBancos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);

  // Cargar catálogos al montar
  useEffect(() => {
    loadCatalogos();
  }, []);

  // Cargar cheques pendientes y reprogramados al montar
  useEffect(() => {
    fetchCheques({ estado: 'pendiente,reprogramado' });
  }, []);

  const loadCatalogos = async () => {
    try {
      setLoadingCatalogos(true);
      const [bancosData, clientesData, cuentasData] = await Promise.all([
        catalogoService.getBancos(),
        catalogoService.getClientes(),
        catalogoService.getCuentasBancarias(),
      ]);

      console.log('Bancos cargados:', bancosData);
      console.log('Clientes cargados:', clientesData);
      console.log('Cuentas cargadas:', cuentasData);

      setBancos(bancosData);
      setClientes(clientesData);
      setCuentasBancarias(cuentasData);
    } catch (error) {
      console.error('Error cargando catálogos:', error);
      showError('Error al cargar catálogos');
    } finally {
      setLoadingCatalogos(false);
    }
  };

  // Manejar selección de cheque
  const handleSelectCheque = (chequeId) => {
    setSelectedCheques((prev) => {
      if (prev.includes(chequeId)) {
        return prev.filter((id) => id !== chequeId);
      } else {
        return [...prev, chequeId];
      }
    });
  };

  // Seleccionar todos los cheques
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allChequeIds = getFilteredCheques().map((cheque) => cheque.id);
      setSelectedCheques(allChequeIds);
    } else {
      setSelectedCheques([]);
    }
  };

  // Obtener cheques filtrados
  const getFilteredCheques = () => {
    return cheques.filter((cheque) => {
      if (filters.banco && cheque.banco?.id !== filters.banco) return false;
      if (filters.cliente && cheque.cliente?.id !== filters.cliente) return false;
      if (filters.fechaPago) {
        const chequeFecha = new Date(cheque.fecha_pago);
        const filterFecha = new Date(filters.fechaPago);
        if (chequeFecha.toDateString() !== filterFecha.toDateString()) return false;
      }
      return true;
    });
  };

  // Calcular total seleccionado
  const getTotalSeleccionado = () => {
    return cheques
      .filter((cheque) => selectedCheques.includes(cheque.id))
      .reduce((sum, cheque) => {
        const monto = parseFloat(cheque.monto);
        return sum + (isNaN(monto) ? 0 : monto);
      }, 0);
  };

  // Obtener cheques seleccionados
  const getSelectedChequesData = () => {
    return cheques.filter((cheque) => selectedCheques.includes(cheque.id));
  };

  // Navegación del wizard
  const handleNext = () => {
    if (activeStep === 0) {
      // Validar que se haya seleccionado al menos 1 cheque
      if (selectedCheques.length === 0) {
        showWarning('Debe seleccionar al menos un cheque');
        return;
      }
    }

    if (activeStep === 1) {
      // Validar información del depósito
      if (!depositData.cuentaBancariaId) {
        showWarning('Debe seleccionar una cuenta bancaria');
        return;
      }
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Crear depósito
  const handleCreateDeposit = async () => {
    try {
      // Obtener el banco de la cuenta bancaria seleccionada
      const cuentaSeleccionada = cuentasBancarias.find(
        (c) => c.id === depositData.cuentaBancariaId
      );

      if (!cuentaSeleccionada || !cuentaSeleccionada.banco_id) {
        showError('No se pudo determinar el banco de la cuenta seleccionada');
        return;
      }

      const data = {
        cheque_ids: selectedCheques,
        cuenta_bancaria_id: depositData.cuentaBancariaId,
        banco_destino_id: cuentaSeleccionada.banco_id,
        fecha_deposito: depositData.fechaDeposito?.format('YYYY-MM-DD'),
        numero_boleta: depositData.numeroBoleta || undefined,
        observaciones: depositData.observaciones || undefined,
      };

      console.log('Datos a enviar al backend:', data);

      const deposito = await createDeposito(data);
      showSuccess('Depósito creado exitosamente');
      navigate(`/depositos/${deposito.id}`);
    } catch (error) {
      console.error('Error completo al crear depósito:', error);
      showError('Error al crear depósito');
    }
  };

  // Formatear moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(value);
  };

  // Formatear fecha
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-AR');
  };

  if (loadingCatalogos) {
    return <LoadingSpinner message="Cargando información..." />;
  }

  // PASO 1: Seleccionar Cheques
  const renderStepSelectCheques = () => {
    const filteredCheques = getFilteredCheques();
    const allSelected =
      filteredCheques.length > 0 &&
      filteredCheques.every((cheque) => selectedCheques.includes(cheque.id));

    return (
      <Box>
        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Banco</InputLabel>
                <Select
                  value={filters.banco}
                  label="Banco"
                  onChange={(e) => setFilters({ ...filters, banco: e.target.value })}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {bancos.map((banco) => (
                    <MenuItem key={banco.id} value={banco.id}>
                      {banco.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Cliente</InputLabel>
                <Select
                  value={filters.cliente}
                  label="Cliente"
                  onChange={(e) => setFilters({ ...filters, cliente: e.target.value })}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {clientes.map((cliente) => (
                    <MenuItem key={cliente.id} value={cliente.id}>
                      {cliente.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                <DatePicker
                  label="Fecha Pago"
                  value={filters.fechaPago}
                  onChange={(newValue) => setFilters({ ...filters, fechaPago: newValue })}
                  slotProps={{
                    textField: { size: 'small', fullWidth: true },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabla de cheques */}
        <Paper>
          {chequesLoading ? (
            <LoadingSpinner message="Cargando cheques..." />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={
                          selectedCheques.length > 0 &&
                          selectedCheques.length < filteredCheques.length
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Número</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Banco</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell>Fecha Pago</TableCell>
                    <TableCell>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCheques.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        No hay cheques disponibles para depositar
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCheques.map((cheque) => (
                      <TableRow
                        key={cheque.id}
                        hover
                        selected={selectedCheques.includes(cheque.id)}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleSelectCheque(cheque.id)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox checked={selectedCheques.includes(cheque.id)} />
                        </TableCell>
                        <TableCell>{cheque.numero_cheque}</TableCell>
                        <TableCell>{cheque.cliente?.nombre || '-'}</TableCell>
                        <TableCell>{cheque.banco?.nombre || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(cheque.monto)}</TableCell>
                        <TableCell>{formatDate(cheque.fecha_pago)}</TableCell>
                        <TableCell>
                          <Chip
                            label={cheque.estado === 'reprogramado' ? 'Reprogramado' : 'Pendiente'}
                            color={cheque.estado === 'reprogramado' ? 'info' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Total seleccionado */}
          {selectedCheques.length > 0 && (
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography variant="body2" color="text.secondary">
                    {selectedCheques.length} cheque(s) seleccionado(s)
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="h6" color="primary">
                    Total: {formatCurrency(getTotalSeleccionado())}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </Box>
    );
  };

  // PASO 2: Información del Depósito
  const renderStepDepositInfo = () => {
    return (
      <Grid container spacing={3}>
        {/* Formulario */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Información del Depósito
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="cuenta-select-label">Cuenta Bancaria Destino</InputLabel>
                  <Select
                    labelId="cuenta-select-label"
                    id="cuenta-select"
                    value={depositData.cuentaBancariaId || ''}
                    label="Cuenta Bancaria Destino"
                    onChange={(e) => {
                      const newValue = e.target.value;
                      console.log('Cuenta seleccionada:', newValue);
                      setDepositData(prev => ({ ...prev, cuentaBancariaId: newValue }));
                    }}
                  >
                    <MenuItem value="">
                      <em>Seleccione una cuenta</em>
                    </MenuItem>
                    {cuentasBancarias.map((cuenta) => (
                      <MenuItem key={cuenta.id} value={cuenta.id}>
                        {cuenta.banco?.nombre || 'Banco'} - {cuenta.numero_cuenta} ({cuenta.tipo_cuenta})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                  <DatePicker
                    label="Fecha de Depósito"
                    value={depositData.fechaDeposito}
                    onChange={(newValue) =>
                      setDepositData({ ...depositData, fechaDeposito: newValue })
                    }
                    slotProps={{
                      textField: { fullWidth: true, required: true },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Número de Comprobante"
                  value={depositData.numeroBoleta}
                  onChange={(e) =>
                    setDepositData({ ...depositData, numeroBoleta: e.target.value })
                  }
                  placeholder="Opcional"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Observaciones"
                  value={depositData.observaciones}
                  onChange={(e) =>
                    setDepositData({ ...depositData, observaciones: e.target.value })
                  }
                  placeholder="Opcional"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Resumen de cheques seleccionados */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Resumen de Cheques
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Cantidad de cheques
                </Typography>
                <Typography variant="h5">{selectedCheques.length}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Monto total
                </Typography>
                <Typography variant="h5" color="primary">
                  {formatCurrency(getTotalSeleccionado())}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // PASO 3: Confirmación
  const renderStepConfirmation = () => {
    const selectedChequesData = getSelectedChequesData();
    const cuentaSeleccionada = cuentasBancarias.find(
      (c) => c.id === depositData.cuentaBancariaId
    );

    return (
      <Box>
        {/* Información del depósito */}
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Información del Depósito
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Cuenta Bancaria Destino
              </Typography>
              <Typography variant="body1">
                {cuentaSeleccionada?.banco?.nombre || 'Banco'} - {cuentaSeleccionada?.numero_cuenta} ({cuentaSeleccionada?.tipo_cuenta})
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Fecha de Depósito
              </Typography>
              <Typography variant="body1">{formatDate(depositData.fechaDeposito)}</Typography>
            </Grid>
            {depositData.numeroBoleta && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Número de Comprobante
                </Typography>
                <Typography variant="body1">{depositData.numeroBoleta}</Typography>
              </Grid>
            )}
            {depositData.observaciones && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Observaciones
                </Typography>
                <Typography variant="body1">{depositData.observaciones}</Typography>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Tabla de cheques incluidos */}
        <Paper sx={{ mb: 2 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">Cheques Incluidos</Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Número</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Banco</TableCell>
                  <TableCell align="right">Monto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedChequesData.map((cheque) => (
                  <TableRow key={cheque.id}>
                    <TableCell>{cheque.numero_cheque}</TableCell>
                    <TableCell>{cheque.cliente?.nombre || '-'}</TableCell>
                    <TableCell>{cheque.banco?.nombre || '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(cheque.monto)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} align="right">
                    <Typography variant="h6">Total:</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="h6" color="primary">
                      {formatCurrency(getTotalSeleccionado())}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Alerta de confirmación */}
        <Alert severity="info">
          Verifique que toda la información sea correcta antes de crear el depósito.
        </Alert>
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <PageHeader
        title="Nuevo Depósito"
        subtitle="Cree un nuevo depósito de cheques en 3 simples pasos"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Depósitos', path: '/depositos' },
          { label: 'Nuevo' },
        ]}
      />

      {/* Stepper */}
      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Contenido del paso actual */}
        <Box sx={{ mb: 3 }}>
          {activeStep === 0 && renderStepSelectCheques()}
          {activeStep === 1 && renderStepDepositInfo()}
          {activeStep === 2 && renderStepConfirmation()}
        </Box>

        {/* Botones de navegación */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button disabled={activeStep === 0} onClick={handleBack}>
            Atrás
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={() => navigate('/depositos')}>
              Cancelar
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleCreateDeposit}
                disabled={depositLoading}
              >
                {depositLoading ? 'Creando...' : 'Crear Depósito'}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNext}>
                Siguiente
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default DepositNewPage;
